<template>
  <div>
    <NavbarTwo />
    <div class="static-hero">
      <div class="container mw-1356">
        <span class="top-title">LEGAL</span>
        <h1>Terms &amp; Conditions</h1>
        <p>The terms that govern your use of {{ c.tradingName }}</p>
      </div>
    </div>
    <div class="static-body bg-gondola">
      <div class="container mw-1356" style="max-width:860px">
        <div class="pp-section">
          <p>
            These Terms &amp; Conditions ("Terms") form a binding agreement between you and
            <strong>{{ c.legalName }}</strong><span v-if="c.registrationNumber"> (registration no. {{ c.registrationNumber }})</span>,
            trading as {{ c.tradingName }} ("we", "us"). By creating an account or making a
            payment you accept these Terms.
          </p>
        </div>

        <div class="pp-section">
          <h2>1. The Service</h2>
          <p>
            {{ c.tradingName }} provides access to internet television (IPTV) streaming
            subscriptions. We grant you a personal, non-transferable, non-exclusive licence to
            access the service for the duration of your paid subscription.
          </p>
        </div>

        <div class="pp-section">
          <h2>2. Eligibility &amp; Legal Restrictions</h2>
          <ul>
            <li>You must be at least <strong>{{ c.minAge }} years old</strong> to purchase a subscription.</li>
            <li>The service is for <strong>private, personal, non-commercial use only</strong>. Public performance, redistribution, resale or sub-licensing of any content is prohibited.</li>
            <li>You may not use the service to infringe any third party's intellectual property or to access content where you do not hold the necessary rights.</li>
            <li>Availability of specific channels and content may be subject to geographic licensing restrictions and can change without notice.</li>
            <li>You are responsible for complying with all laws and broadcasting regulations applicable in your country of residence.</li>
          </ul>
        </div>

        <div class="pp-section">
          <h2>3. Subscriptions, Pricing &amp; Billing</h2>
          <ul>
            <li>All prices are listed and charged in <strong>{{ c.payments.currency }}</strong>. The exact amount and currency are shown before you confirm payment.</li>
            <li>Card payments are processed by our payment facilitator, <strong>{{ c.payments.facilitator.name }}</strong><template v-if="c.payments.descriptor">, and appear on your statement as <strong>{{ c.payments.descriptor }}</strong></template>.</li>
            <li>Subscriptions are prepaid for the selected term and activate once payment is confirmed.</li>
            <li>We accept {{ schemeList }} and other methods shown at checkout.</li>
          </ul>
        </div>

        <div class="pp-section">
          <h2>4. Refunds</h2>
          <p>
            Refunds are governed by our <RouterLink to="/refund-policy">Return &amp; Refund Policy</RouterLink>.
            In summary, you may request a refund within <strong>{{ c.refund.windowDays }} days</strong> of
            purchase if the service cannot be made to work for you; see the full policy for eligibility and exclusions.
          </p>
        </div>

        <div class="pp-section">
          <h2>5. Account &amp; Security</h2>
          <p>
            You are responsible for keeping your account credentials and streaming details
            confidential. Sharing your subscription across multiple households or simultaneous
            unauthorised connections may result in suspension without refund.
          </p>
        </div>

        <div class="pp-section">
          <h2>6. Intellectual Property</h2>
          <p>
            All trademarks, logos and content remain the property of their respective owners.
            Nothing in these Terms transfers any ownership rights to you.
          </p>
        </div>

        <div class="pp-section">
          <h2>7. Suspension &amp; Termination</h2>
          <p>
            We may suspend or terminate your access if you breach these Terms, attempt fraud or
            chargebacks in bad faith, or misuse the service. Where termination is due to your
            breach, no refund is due.
          </p>
        </div>

        <div class="pp-section">
          <h2>8. Limitation of Liability</h2>
          <p>
            The service is provided "as is". To the maximum extent permitted by law, our total
            liability for any claim is limited to the amount you paid for the subscription in the
            three months preceding the claim. We are not liable for interruptions caused by your
            internet connection, devices, or upstream content providers.
          </p>
        </div>

        <div class="pp-section">
          <h2>9. Governing Law</h2>
          <p v-if="hasJurisdiction">These Terms are governed by the laws of {{ c.jurisdiction }}.</p>
          <p v-else>These Terms are governed by the laws of the country in which {{ c.tradingName }} is established.</p>
        </div>

        <div class="pp-section">
          <h2>10. Business Details &amp; Contact</h2>
          <p>
            {{ c.legalName }}<br />
            {{ address }}<br />
            <span v-if="c.support.email">Email: <a :href="'mailto:' + c.support.email">{{ c.support.email }}</a></span>
            <span v-if="c.support.phone"> · Phone: {{ c.support.phone }}</span>
          </p>
          <p>For any questions, visit our <RouterLink to="/contact-us">Contact Us</RouterLink> page.</p>
        </div>
      </div>
    </div>
    <CopyRight />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import NavbarTwo from "@/components/Layouts/NavbarTwo.vue";
import CopyRight from "@/components/Layouts/CopyRight.vue";
import { company, establishmentLine } from "@/config/company";

export default defineComponent({
  name: "TermsConditionsPage",
  components: { NavbarTwo, CopyRight },
  setup() {
    const schemeList = company.payments.schemes
      .map((s) => ({ visa: "Visa", mastercard: "Mastercard", amex: "American Express" }[s] || s))
      .join(", ");
    const hasJurisdiction = !/^TODO/i.test(company.jurisdiction.trim());
    return { c: company, address: establishmentLine(), schemeList, hasJurisdiction };
  },
});
</script>

<style scoped>
.static-hero {
  background: linear-gradient(135deg, #1a0a0b 0%, #2d1214 100%);
  padding: 120px 0 60px;
  text-align: center;
}
.static-hero .top-title {
  display: inline-block;
  background: rgba(201, 14, 14, 0.15);
  color: #e84749;
  border: 1px solid rgba(201, 14, 14, 0.3);
  padding: 0.3rem 1rem;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 2px;
  margin-bottom: 1rem;
}
.static-hero h1 {
  color: #fff;
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 900;
  margin-bottom: 0.75rem;
}
.static-hero p {
  color: #c0c0c0;
  font-size: 1.1rem;
}
.static-body {
  padding: 80px 0 100px;
}
.bg-gondola {
  background: #120a0b;
}
.pp-section {
  margin-bottom: 2.5rem;
}
.pp-section h2 {
  color: #fff;
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #2d1214;
}
.pp-section p,
.pp-section li {
  color: #c0c0c0;
  line-height: 1.8;
  font-size: 0.95rem;
}
.pp-section ul {
  padding-left: 1.5rem;
}
.pp-section ul li {
  margin-bottom: 0.4rem;
}
.pp-section a {
  color: #e84749;
  text-decoration: none;
}
.pp-section a:hover {
  text-decoration: underline;
}
</style>
